import axios from "axios";
import { setCookie, base64UrlToBase64 } from "./utils";
import logger from "./logger";
const ENDPOINTS = {
    USER_PROFILE: "/assets/v2/Me",
    AUTH: "/auth/",
    CONSENT_SIGNUP: "/consent/gaming/v1-1/enterprise/v1-1?trackTechnicalData&trackBehavioralData",
    CONSENT_SIGNUP_GAME: "/consent/gaming/v1-1?trackTechnicalData&trackBehavioralData&region=CN",
    CONSENT_SIGNUP_ENTERPRISE:
        "/consent/enterprise/v1-1?trackTechnicalData&trackBehavioralData&region=CN",
};

const consentMapping = {
    gaming: "gaming",
    developer: "enterprise",
    enterprise: "enterprise",
    developersbusiness: "enterprise",
};
// honoring login hints for 1 hour
// subject to change
const HINTS_EXPIRATION = 3600;

// default sso timeout
// used when starfleet class is not initiated with a timeout value
const DEFAULT_SSO_TIMEOUT = 3000;

let HAS_JSO = !!sessionStorage.getItem("jso");
export default class Starfleet {
    constructor(configs) {
        this.configs = configs;
    }

    authorize(ui_locales, prompt, login_hint = null) {
        localStorage.removeItem(this.configs.SESSION_ITEM_NAME);
        localStorage.removeItem(this.configs.LAST_LOGIN);
        localStorage.removeItem(this.configs.PRIVACY_ITEM_NAME);
        const [lang, region] = ui_locales.split("-");
        const searchParams = new URLSearchParams({
            redirect_uri: location.href,
            client_id: this.configs.CLIENT_ID,
            idp_id: this.configs.JARVIS_IDP_ID,
            ui_locales: `${lang}-${region.toUpperCase()}`,
        });
        if (prompt) {
            searchParams.set("prompt", prompt);
        }
        if (login_hint) {
            searchParams.set("login_hint", login_hint);
        }
        window.location.href = `${ENDPOINTS.AUTH}?${searchParams.toString()}`;
    }

    getLocalSession(resolve) {
        logger.debug("getLocalSession: getting local session from the below item");
        logger.debug(this.configs.SESSION_ITEM_NAME);
        const session = JSON.parse(localStorage.getItem(this.configs.SESSION_ITEM_NAME));
        if (session) {
            logger.debug("getLocalSession: session found in local storage");
            logger.debug(session);
            if (new Date(session.expiration) > new Date()) {
                resolve(session);
                return true;
            } else {
                logger.debug("getLocalSession: session expired");
                return false;
            }
        } else return false;
    }

    getSsoSession(resolve) {
        const ssoSession = JSON.parse(
            localStorage.getItem(localStorage.getItem(this.configs.LAST_LOGIN))
        );

        if (
            ssoSession &&
            this.configs.SUPPORTED_IDPS.includes(ssoSession.idp_id) &&
            new Date(ssoSession.expiration) > new Date()
        ) {
            resolve(ssoSession);
            return true;
        } else {
            return false;
        }
    }

    getAuthHints() {
        const login_hint = this.configs.urlParams.get("loginhint");
        if (login_hint) {
            this.configs.urlParams.delete("loginhint");
            window.history.replaceState(
                null,
                null,
                `${
                    Array.from(this.configs.urlParams.values()).length > 0 ? "?" : location.pathname
                }${this.configs.urlParams.toString()}`
            );

            return { timestamp: new Date().toISOString(), login_hint, idp_id: CLIENT_ID };
        }
        const jso = this.configs.urlParams.get("jso");
        if (jso) {
            try {
                logger.debug("jso passed: ", jso);
                HAS_JSO = true;
                sessionStorage.setItem("jso", jso);
                const parsedJso = JSON.parse(atob(base64UrlToBase64(jso)));
                this.configs.urlParams.delete("jso");
                window.history.replaceState(
                    null,
                    null,
                    `${
                        Array.from(this.configs.urlParams.values()).length > 0
                            ? "?"
                            : location.pathname
                    }${this.configs.urlParams.toString()}`
                );
                // if the JSO is missing the login hint, trigger error and return the local storage login hints
                if (!parsedJso.loginHint) {
                    throw "jso missing login hint";
                }
                return {
                    timestamp: new Date().toISOString(),
                    login_hint: parsedJso.loginHint,
                    idp_id: parsedJso.idpId,
                };
            } catch (err) {
                logger.debug("error parsing the passing jso param", err);
            }
        }
        return JSON.parse(localStorage.getItem(this.configs.LOGIN_HINTS_ITEM_NAME));
    }
    async getAuthHintsSession(ui_locales) {
        return new Promise((resolve) => {
            const authHints = this.getAuthHints();
            logger.debug("getAuthHintsSession: auth hints");
            logger.debug(authHints);
            // making sure authhints contain the login hint and still valid before performing the silent login
            if (
                authHints &&
                authHints.login_hint &&
                new Date(authHints.timestamp).getTime() + HINTS_EXPIRATION * 1000 >
                    new Date().getTime()
            ) {
                logger.debug("getAuthHintsSession: auth hints valid, starting silent login");
                const iframe = document.createElement("iframe");
                const [lang, region] = ui_locales.split("-");
                const searchParams = new URLSearchParams({
                    redirect_uri: location.href,
                    client_id: this.configs.CLIENT_ID,
                    idp_id: JARVIS_IDP_ID,
                    ui_locales: `${lang}-${region.toUpperCase()}`,
                    prompt: "none",
                    login_hint: authHints.login_hint,
                });
                iframe.src = `${location.origin}${ENDPOINTS.AUTH}?${searchParams.toString()}`;
                logger.debug("iframe URL", iframe.src);
                iframe.hidden = true;
                const timeoutID = setTimeout(() => {
                    logger.debug("silent login timed out");
                    resolve(false);
                }, this.configs.SSO_TIMEOUT || DEFAULT_SSO_TIMEOUT);
                window.addEventListener("message", (event) => {
                    if (event?.data === "success") {
                        logger.debug("getAuthHintsSession: silent login successful");
                        clearTimeout(timeoutID);
                        document.body.removeChild(iframeElement);
                        resolve(JSON.parse(localStorage.getItem(this.configs.SESSION_ITEM_NAME)));
                    } else if (event?.data === "failure") {
                        clearTimeout(timeoutID);
                        document.body.removeChild(iframeElement);
                        logger.debug("silent login failed");
                        resolve(false);
                    }
                });
                const iframeElement = document.body.appendChild(iframe);
            } else {
                logger.debug("getAuthHintsSession: auth hints not valid");
                resolve(false);
            }
        });
    }

    /**
     * @name getSession
     * @description Function that fetches the user session info based on SSO logic
     * @param {string} ui_locales
     *
     * @returns {Promise} session data
     */
    async getSession(ui_locales) {
        // when jso query param is present on the page, the user is linked directly from the GFN/GFE/NVAPP client.
        // when loginhint param is present on the page, the user is linked directly from GFE/NVAPP. It will be deprecated

        if (!this.configs.urlParams.has("jso") && !this.configs.urlParams.has("loginhint")) {
            // when jso or loginhint params are not present in the URL, we start with the local session
            logger.debug("getSession: no jso or loginhint params, starting with local session");
            return new Promise((resolve) => {
                (async () => {
                    if (this.getLocalSession(resolve)) {
                        return;
                    }
                    const session = await this.getAuthHintsSession(ui_locales);
                    resolve(session);
                })();
            });
        } else {
            logger.debug(
                "getSession: jso or loginhint params present, starting with auth hints session"
            );
            return new Promise((resolve) => {
                (async () => {
                    const session = await this.getAuthHintsSession(ui_locales);
                    if (session) {
                        resolve(session);
                        return;
                    } else if (this.getLocalSession(resolve)) {
                        return;
                    }
                    resolve(false);
                })();
            });
        }
    }

    getProfile(accessToken) {
        return axios.get(`${this.configs.STARFLEET_BASE}${ENDPOINTS.USER_PROFILE}`, {
            params: {},
            headers: { Authorization: `Bearer ${accessToken}` },
        });
    }

    // function that calls voltron to get the email first party token
    getEmailToken(email) {
        axios
            .get(`${this.configs.VOLTRON_BASE}${EMAIL_TOKEN_URL}`, {
                params: { email },
            })
            .then((response) => {
                setCookie(response.data.token, EMAIL_TOKEN_COOKIE);
            })
            .catch((error) => logger.debug("Getting email token", error));
    }

    // function that calls voltron to get the starfleet first party token
    getStarfleetToken(idToken) {
        axios
            .get(`${this.configs.VOLTRON_BASE}${STARFLEET_TOKEN_URL}`, {
                headers: { idToken },
            })
            .then((response) => {
                setCookie(response.data.token, STARFLEET_TOKEN_COOKIE);
            })
            .catch((error) => logger.debug("Getting starfleet token", error));
    }

    // function that generates an anonymous first party token and saves it to the cookies
    // the function was shared by AEM team
    getanonymousToken() {
        try {
            let d = new Date().getTime(); //Timestamp
            let d2 = (performance && performance.now && performance.now() * 1000) || 0; //Time in microseconds since page-load or 0 if unsupported
            const anonymousToken = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
                /[xy]/g,
                function (c) {
                    var r = Math.random() * 16; //random number between 0 and 16
                    if (d > 0) {
                        //Use timestamp until depleted
                        r = (d + r) % 16 | 0;
                        d = Math.floor(d / 16);
                    } else {
                        //Use microseconds since page-load if supported
                        r = (d2 + r) % 16 | 0;
                        d2 = Math.floor(d2 / 16);
                    }
                    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
                }
            );
            setCookie(anonymousToken, ANONYMOUS_TOKEN_COOKIE);
        } catch (err) {
            logger.debug("Getting anomymous token", err);
        }
    }

    consentSignupCheck(consents = [], userId, locale = "en-us", options) {
        const { tld = "com", countryCode = "US", isPIPLM4On = true } = options;
        const parsedConsent = {};
        const serviceTypeRegex = /\w+(?=\/v)/;

        let serviceType;

        // the backend happens to send consents not sorted
        // this is sorting the consent array by timestamp
        consents.sort((memo, item) => new Date(memo.timestamp) - new Date(item.timestamp));

        for (const consent of consents) {
            try {
                serviceType = consent.consentAsked.match(serviceTypeRegex)[0];
                parsedConsent[`${consentMapping[serviceType]}`] = {
                    consentGranted: consent.consentGranted,
                };
            } catch (err) {
                logger.debug("consentAsked URL is not matching the spec", err);
            }
        }
        // read sessionStorage to get list of userIds that were asked to complete their consent
        const consentAsked = sessionStorage.getItem("consentAsked")?.split(",") || [];

        logger.debug(parsedConsent);

        if (
            Object.keys(parsedConsent).length === 2 ||
            consentAsked.includes(userId.substring(0, 6)) ||
            HAS_JSO
        ) {
            // if the user has one of the following
            // ** consented to our 2 types of consent
            // ** was already presented with the consent screen
            // ** coming from the GFN app ( with the presence of the jso query param )
            // we let the user proceed to the account page
            sessionStorage.setItem(PARSED_CONSENT_ITEM_NAME, JSON.stringify(parsedConsent));
            return true;
        } else {
            const [lang, region] = locale.split("-");
            // the user has only one type of consent max and was not presented with the consent screen during this active session
            consentAsked.push(userId.substring(0, 6));
            sessionStorage.setItem("consentAsked", consentAsked.join());
            history.pushState({}, "", location.href);
            let consentUrl = "";
            if (tld === "com" && countryCode === "CN") {
                consentUrl = isPIPLM4On
                    ? ENDPOINTS.CONSENT_SIGNUP
                    : ENDPOINTS.CONSENT_SIGNUP_ENTERPRISE;
            } else if (tld === "cn") {
                consentUrl = isPIPLM4On ? ENDPOINTS.CONSENT_SIGNUP : ENDPOINTS.CONSENT_SIGNUP_GAME;
            } else {
                consentUrl = ENDPOINTS.CONSENT_SIGNUP;
            }
            location.href = `${consentUrl}&client_id=${
                this.configs.CLIENT_ID
            }&redirect_uri=${encodeURIComponent(
                location.href
            )}&locale=${lang}_${region.toUpperCase()}`;
            sessionStorage.setItem(PARSED_CONSENT_ITEM_NAME, false);

            return false;
        }
    }
}
